// form-validation-service.es6
import flatpickr from "flatpickr";
import { German } from "flatpickr/dist/l10n/de.js";

export default class FormValidationService {
    constructor(config = {}) {
        this.selectors = config.selectors || {};
        this.rules = config.rules || {};
        this.messages = config.messages || {};
        this.flatpickrItems = config.flatpickrItems || [];

        this.onValidSubmit = config.onValidSubmit || null;
        this.onAfterHeightChange = config.onAfterHeightChange || null;
    }

    init() {
        this.setupValidationMessages();
        this.setupValidationMethods();
        this.initFlatpickr();
        this.setupFormValidation();
        this.bindFieldUiHandlers();
        this.checkFields();
    }

    // -----------------------------
    // Messages
    // -----------------------------
    setupValidationMessages() {
        $.extend($.validator.messages, {
            required: "Dieses Feld wird benötigt.",
            email: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
            number: "Bitte geben Sie eine gültige Zahl ein.",
            range: $.validator.format("Bitte geben Sie einen Wert zwischen {0} und {1} ein."),
            max: $.validator.format("Bitte geben Sie einen Wert kleiner oder gleich {0} ein."),
            min: $.validator.format("Bitte geben Sie einen Wert größer oder gleich {0} ein."),
            digits: "Nur Zahlen sind erlaubt.",
            rangelength: $.validator.format("Bitte geben Sie genau {0} Zeichen ein.")
        });
    }

    // -----------------------------
    // Methods
    // -----------------------------
    setupValidationMethods() {
        $.validator.addMethod("lettersDigitsHyphen", function (value, element) {
            return this.optional(element) || /^[a-zA-Z0-9-]+$/.test(value);
        }, "Bitte geben Sie nur Buchstaben, Zahlen und Bindestriche ein.");

        $.validator.addMethod("dateFuture", function(value, element) {
            const selectedDate = new Date(value);
            const currentDate = new Date();
            selectedDate.setHours(0, 0, 0, 0);
            currentDate.setHours(0, 0, 0, 0);
            return this.optional(element) || selectedDate > currentDate;
        }, "Datum muss in der Zukunft liegen");

        $.validator.addMethod("ibanLength", function(value) {
            const valueWithoutSpaces = String(value || '').replace(/\s+/g, '');
            const valueWithoutLines = valueWithoutSpaces.replace('_', '');
            return valueWithoutLines.length === 22;
        }, "IBAN muss 22 Zeichen lang sein");

        $.validator.addMethod("germanIbanPrefix", function(value) {
            const valueWithoutSpaces = String(value || '').replace(/\s+/g, '');
            return /^DE/.test(valueWithoutSpaces);
        }, "Bitte gib eine deutsche IBAN an");

        $.validator.addMethod("germanIbanValidation", function(value) {
            const iban = String(value || '').replace(/\s+/g, '').toUpperCase();
            if (!/^DE\d{20}$/.test(iban)) return false;

            const rearranged = iban.slice(4) + iban.slice(0, 4);
            let numericIban = "";

            for (let i = 0; i < rearranged.length; i++) {
                const ch = rearranged.charAt(i);
                if (ch >= 'A' && ch <= 'Z') numericIban += (ch.charCodeAt(0) - 55);
                else numericIban += ch;
            }

            let remainder = numericIban;
            while (remainder.length > 2) {
                const block = remainder.substring(0, 9);
                remainder = (parseInt(block, 10) % 97).toString() + remainder.substring(block.length);
            }

            return parseInt(remainder, 10) % 97 === 1;
        }, "Die eingegebene IBAN ist ungültig, bitte überprüfe Deine Eingabe");

        $.validator.addMethod("notSoonerThan14Days", function (value, element) {
            if (this.optional(element)) return true;

            const selectedDate = new Date(value);
            if (isNaN(selectedDate)) return false;

            selectedDate.setHours(0, 0, 0, 0);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const minAllowed = new Date(today);
            minAllowed.setDate(minAllowed.getDate() + 14);

            return selectedDate >= minAllowed;
        }, "Dein frühstmögliches Kündigungsdatum muss mindestens 14 Tage in der Zukunft liegen.");

        $.validator.addMethod("dateOfBirthRange", function(value, element, params) {
            const selectedDate = new Date(value);

            const maxDate = new Date();
            maxDate.setFullYear(maxDate.getFullYear() - 18);
            maxDate.setHours(0, 0, 0, 0);

            const minDate = new Date();
            minDate.setFullYear(minDate.getFullYear() - 130);
            minDate.setHours(0, 0, 0, 0);

            return this.optional(element) || (selectedDate >= minDate && selectedDate <= maxDate);
        }, "Ihr Alter muss über 18 oder unter 130 Jahre liegen.");
    }

    // -----------------------------
    // Flatpickr (optional)
    // -----------------------------
    initFlatpickr() {
        if (!this.flatpickrItems.length) return;

        this.flatpickrItems.forEach(item => {
            this.setupFlatpickr(item.selector, item.options || {});
        });
    }

    setupFlatpickr(selector, additionalOptions = {}) {
        const { FIELD_CONTAINER, DATE_ICON } = this.selectors;

        setTimeout(() => {
            $(selector).each(function () {
                const $pickerElement = $(this);

                const fp = $pickerElement.flatpickr({
                    locale: German,
                    altInput: true,
                    altFormat: "d.m.Y",
                    dateFormat: "Y-m-d",
                    allowInput: true,
                    clickOpens: false,
                    disableMobile: true,
                    onValueUpdate(selectedDates, dateStr, instance){ $(instance.input).valid(); },
                    onClose(selectedDates, dateStr, instance){ $(instance.input).valid(); },
                    ...additionalOptions
                });

                if (FIELD_CONTAINER && DATE_ICON) {
                    $pickerElement.closest(FIELD_CONTAINER).find(DATE_ICON).on("click", function () {
                        fp.open();
                    });
                }
            });
        }, 10);
    }

    // -----------------------------
    // jQuery Validate
    // -----------------------------
    setupFormValidation() {
        const {
            FORM,
            FIELD_CONTAINER
        } = this.selectors;

        const $form = $(FORM);
        if (!$form.length) return;

        const safeHeight = () => {
            if (typeof this.onAfterHeightChange === 'function') {
                this.onAfterHeightChange();
            }
        };

        $form.validate({
            ignore: ".datepicker.input",
            rules: this.rules,
            messages: this.messages,

            errorPlacement: (error, element) => {
                error.appendTo(element.closest('.field-wrap').find('.nf-error-msg'));
                safeHeight();
            },
            highlight: (element) => {
                $(element).closest('.field-wrap').addClass('nf-error').removeClass('nf-pass');
                safeHeight();
            },
            unhighlight: (element) => {
                $(element).closest('.field-wrap').removeClass('nf-error');
                safeHeight();
            },
            success: (label, element) => {
                $(element).closest('.field-wrap').addClass('nf-pass');
                safeHeight();
            },
            submitHandler: (form, event) => {
                if (event) event.preventDefault();
                if (typeof this.onValidSubmit === 'function') {
                    this.onValidSubmit(form, event);
                }
            }
        });
    }

    // -----------------------------
    // UI label logic (shared)
    // -----------------------------
    bindFieldUiHandlers() {
        const {
            ALL_INPUTS,
            FIELD_CONTAINER,
            NINJA_FORMS_FIELD,
            DATE_INPUT,
            DATE_LABEL
        } = this.selectors;

        if (DATE_LABEL) {
            $(document).on('click', DATE_LABEL, (e) => this.handleDateLabelClick(e));
        }

        if (ALL_INPUTS) {
            $(document).on('keyup blur change', ALL_INPUTS, (e) => this.handleInputValidation(e));
        }

        if (NINJA_FORMS_FIELD) {
            $(document).on('input', NINJA_FORMS_FIELD, (e) => this.handleInputEvent(e));
            $(document).on('focus', NINJA_FORMS_FIELD, (e) => this.handleFocusEvent(e));
            $(document).on('blur',  NINJA_FORMS_FIELD, (e) => this.handleBlurEvent(e));
        }

        if (DATE_INPUT) {
            $(document).on('focus', DATE_INPUT, (e) => this.handleDateInputFocus(e));
            $(document).on('blur',  DATE_INPUT, (e) => this.handleDateInputBlur(e));
        }
    }

    handleDateLabelClick(event) {
        event.preventDefault();

        const $label = $(event.currentTarget);
        const fieldId = $label.attr('for');
        let $currentField = $(`#${fieldId}`);

        if ($currentField.length && $currentField.attr('type') === 'hidden') {
            $currentField = $currentField.next();
        }

        $currentField.focus();
    }

    handleInputValidation(event) {
        const { FORM, FIELD_CONTAINER } = this.selectors;

        let $input = $(event.currentTarget);
        const $attr = $input.attr('required');
        const $container = $input.closest(FIELD_CONTAINER);
        const $wrap = $input.closest('.field-wrap');

        if ($input.length && $input.attr('type') === 'hidden') {
            $input = $input.next();
        }

        const v = $(FORM).data('validator');
        if (v) v.element($input[0]);

        if (($input.is('[type="text"]') || $input.is('[type="number"]') || $input.is('[type="tel"]') || $input.is('[type="date"]')) && !$attr) {
            if (String($input.val() || '').trim() !== '') {
                $container.addClass('fake-nf-pass');
            } else {
                $container.removeClass('fake-nf-pass');
                $wrap.removeClass('nf-pass');
            }
        }
    }

    handleInputEvent(event) {
        const { FIELD_CONTAINER } = this.selectors;

        const $input = $(event.currentTarget);
        const $attr = $input.attr('required');
        const $container = $input.closest(FIELD_CONTAINER);

        let value = $input.val();

        if ($input.is('select[multiple]')) {
            value = Array.isArray(value) && value.length > 0 ? value.join('').trim() : '';
        } else if ($input.is('select')) {
            value = value ? String(value).trim() : '';
        } else if (typeof value === 'string') {
            value = value.trim();
        } else {
            value = '';
        }

        $container.removeClass('potentially-required-label');

        if (value !== '') {
            $container.addClass('label-up');
            if ($attr) $container.removeClass('required-label');
        } else {
            $container.removeClass('label-up');
            if ($attr) $container.addClass('required-label');
        }
    }

    handleFocusEvent(event) {
        const { FIELD_CONTAINER } = this.selectors;
        const $input = $(event.currentTarget);
        const $container = $input.closest(FIELD_CONTAINER);

        $container.addClass('label-focus');
        $container.removeClass('potentially-required-label');
    }

    handleBlurEvent(event) {
        const { FIELD_CONTAINER } = this.selectors;

        const $input = $(event.currentTarget);
        const $attr = $input.attr('required');
        const $container = $input.closest(FIELD_CONTAINER);

        $container.removeClass('potentially-required-label');
        $container.removeClass('label-focus');

        if (String($input.val() || '').trim() === '') {
            if ($attr) $container.addClass('required-label');
        } else {
            if ($attr) $container.removeClass('required-label');
        }
    }

    checkFields() {
        const { ALL_INPUTS, FIELD_CONTAINER, FORM } = this.selectors;

        $(ALL_INPUTS).each(function () {
            const $input = $(this);
            const $attr = $input.attr('required');
            const $container = $input.closest(FIELD_CONTAINER);
            const v = $(FORM).data('validator');

            $container.addClass('active-label');

            if (!$input.is('select') && !$input.is('[type=checkbox]') && !$input.is('[type=radio]')) {
                if (String($input.val() || '').trim() !== '') $container.addClass('label-up');
                else $container.removeClass('label-up');
            }

            if ($attr && ((!$.isArray($input.val()) && String($input.val() || '').trim() === '') || ($.isArray($input.val()) && $input.val().length > 0))) {
                $container.addClass('potentially-required-label');
            }

            if (($input.is('[type="text"]') || $input.is('[type="email"]') || $input.is('[type="tel"]') || $input.is('[type="number"]') || $input.is('[type="date"]')) && String($input.val() || '').trim() !== '') {
                if (v) v.element($input[0]);
            }

            if (($input.is('[type="text"]') || $input.is('[type="number"]') || $input.is('[type="tel"]') || $input.is('[type="date"]')) && !$attr && String($input.val() || '').trim() !== '') {
                $container.addClass('fake-nf-pass');
            }
        });
    }

    handleDateInputFocus(event) {
        const $input = $(event.currentTarget);
        $input.inputmask({
            mask: "99.99.9999",
            placeholder: "tt.mm.jjjj",
            clearIncomplete: true,
        });
    }

    handleDateInputBlur(event) {
        const $input = $(event.currentTarget);
        $input.inputmask('remove');
        $input.attr('placeholder', '');
    }
}
